/**
 * Phase 32 — alerts and digests per country: a Telegram chat, or a thread
 * in the shared Slack channel. Topics are the feed events they carry (the
 * words are the feed's own). A morning digest goes at a recipient-local
 * time and a day-end digest at the window's close.
 * For Media Devoted | Created By Ciro Autiero
 */

export const DROP_GEN_NOTIFICATION_CHANNELS = ["TELEGRAM", "SLACK"] as const;
export type DropGenNotificationChannel =
  (typeof DROP_GEN_NOTIFICATION_CHANNELS)[number];

export const DROP_GEN_NOTIFICATION_TOPICS = [
  "errors",
  "layout",
  "verdicts",
  "halts",
  "gateways",
  "shadow",
  "scaling",
  "kill",
] as const;
export type DropGenNotificationTopic =
  (typeof DROP_GEN_NOTIFICATION_TOPICS)[number];

/** The feed events each topic carries — one place, read by the dispatcher and the editor. */
export const DROP_GEN_TOPIC_EVENTS: Record<
  DropGenNotificationTopic,
  readonly string[]
> = {
  // 19/10/2026 — every error the automation hits (the trace's own feed words)
  // 21/10/2026 — and when one is fixed ("error_resolved": texts filled, a step that ran again)
  errors: ["send_failed", "creative_failed", "automation_error", "error_resolved"],
  layout: ["day_generated"],
  verdicts: ["list_burned", "group_stopped", "list_returned", "pool_reset", "list_repicked"],
  halts: [
    "autosend_halted",
    "autosend_resumed",
    "autosend_held",
    "autosend_missed",
  ],
  gateways: ["gateway_blocked", "experiment_done", "experiment_applied"],
  shadow: ["autosend_shadow", "autosend_shadow_halt"],
  scaling: ["scale_up", "scale_down", "scale_capped", "hit_again"],
  kill: ["kill_on", "kill_off"],
};

export const DROP_GEN_TOPIC_WORDS: Record<
  DropGenNotificationTopic,
  { label: string; hint: string }
> = {
  errors: {
    label: "Errors — every one",
    hint: "Every error the automation hits: a send SMSWiser refused, texts not filled, a tracker that could not be read, a scheduled step that failed — and a ✅ line when one is fixed. Each one is traced on the Errors tab.",
  },
  layout: {
    label: "The day laid out",
    hint: "When the day's drafts are generated or re-synced.",
  },
  verdicts: {
    label: "Burns and stops",
    hint: "A list judged terrible and burned, a group stopped for the day, a list back from its rest, a pool reset.",
  },
  halts: {
    label: "Auto-send halts and sends that need a person",
    hint: "A stop-loss or technical halt, a resume, a draft held or missed. (A failed send is an error — the Errors topic.)",
  },
  gateways: {
    label: "Gateway blocks and experiments",
    hint: "A gateway judged terrible and blocked for the day; an experiment that ended or was applied.",
  },
  shadow: {
    label: "Shadow mode lines",
    hint: "What auto-send would have sent or halted while Simulated.",
  },
  scaling: {
    label: "Scaling steps",
    hint: "The next drops grew, shrank, or hit the cap.",
  },
  kill: {
    label: "Kill switch",
    hint: "The country's kill switch turned on or off.",
  },
};

export interface DropGenNotificationRow {
  channel: DropGenNotificationChannel;
  enabled: boolean;
  /** A target is saved (shown masked, never returned whole). */
  hasTarget: boolean;
  /** Telegram: the chat id, masked. Slack: the webhook, masked. */
  targetMasked: string | null;
  /** Telegram: the bot token is set. Slack: always — the webhook is the secret. */
  channelReady: boolean;
  /** Slack: a thread link is saved, so alerts reply there. */
  hasThread: boolean;
  topics: DropGenNotificationTopic[];
  /** Morning digest, recipient-local minutes from midnight; null = off. */
  digestAtMin: number | null;
  dayEndDigest: boolean;
  lastSentAt: string | null;
  lastError: string | null;
}

export interface DropGenNotificationUpdate {
  enabled?: boolean;
  /** Telegram: a new chat id. Slack: the incoming-webhook URL. Absent = keep. */
  target?: string;
  /** Slack: the link of this country's message. Absent = keep. Empty clears the thread. */
  threadLink?: string | null;
  topics?: DropGenNotificationTopic[];
  digestAtMin?: number | null;
  dayEndDigest?: boolean;
}

/** What a test or a dispatch answers. */
export interface DropGenNotificationSendResult {
  ok: boolean;
  error: string | null;
}

/** The feed events of a set of topics — for the dispatcher's filter. */
export function eventsOfTopics(
  topics: readonly string[],
): ReadonlySet<string> {
  const out = new Set<string>();
  for (const topic of topics) {
    for (const event of DROP_GEN_TOPIC_EVENTS[
      topic as DropGenNotificationTopic
    ] ?? [])
      out.add(event);
  }
  return out;
}

/** Tolerant read of the stored topics: unknown ones dropped. */
export function normalizeTopics(raw: unknown): DropGenNotificationTopic[] {
  if (!Array.isArray(raw)) return [];
  const known = new Set<string>(DROP_GEN_NOTIFICATION_TOPICS);
  return DROP_GEN_NOTIFICATION_TOPICS.filter(
    (t) => raw.includes(t) && known.has(t),
  );
}
